import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import {
  CheckCircle, MapPin, QrCode, Loader2, Navigation,
  AlertCircle, Clock, Target, Zap
} from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

export default function CheckInPage() {
  const { api } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(null);
  const [geoStatus, setGeoStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [showQR, setShowQR] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await api.get('/bookings/active');
      setBookings(res.data);
    } catch (err) {
      console.error('Fetch active bookings error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const checkInWithGeo = async (booking) => {
    setCheckingIn(booking.id);
    setResult(null);
    setGeoStatus('Getting location...');

    if (!navigator.geolocation) {
      setGeoStatus('Geolocation not supported. Use QR code instead.');
      setCheckingIn(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        setGeoStatus('Verifying location...');
        try {
          const res = await api.post(`/bookings/${booking.id}/checkin`, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setResult(res.data);
          fetchBookings();
        } catch (err) {
          setGeoStatus(err.response?.data?.detail || 'Check-in failed');
        } finally {
          setCheckingIn(null);
        }
      },
      (error) => {
        setGeoStatus(`Location error: ${error.message}. Try QR code instead.`);
        setCheckingIn(null);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const checkInWithQR = async (booking) => {
    setCheckingIn(booking.id);
    setResult(null);
    try {
      const res = await api.post(`/bookings/${booking.id}/checkin`, {
        qr_code: booking.qr_code,
      });
      setResult(res.data);
      fetchBookings();
    } catch (err) {
      setGeoStatus(err.response?.data?.detail || 'Check-in failed');
    } finally {
      setCheckingIn(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="checkin-page" className="space-y-6">
      <motion.div {...fadeUp}>
        <h1 className="font-barlow text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <CheckCircle size={28} className="text-lime-400" />
          Check-In
        </h1>
        <p className="text-slate-400 text-sm font-manrope mt-0.5">
          Verify attendance via GPS or QR code to reclaim your stake + bonus
        </p>
      </motion.div>

      {/* Success Result */}
      {result && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="bg-lime-500/10 border border-lime-400/30 rounded-xl p-5 shadow-[0_0_30px_rgba(190,242,100,0.1)]"
          data-testid="checkin-success"
        >
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={20} className="text-lime-400" />
            <span className="font-barlow text-lg font-bold text-lime-400">Check-In Successful!</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider">Stake Released</p>
              <p className="font-barlow text-2xl font-bold text-white">+{result.stake_released} $PLAY</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider">Attendance Bonus</p>
              <p className="font-barlow text-2xl font-bold text-lime-400">+{result.bonus_earned} $PLAY</p>
            </div>
          </div>
          <p className="text-slate-300 text-sm mt-3">{result.message}</p>
          <p className="text-slate-500 text-xs mt-1">Method: {result.method?.replace('_', ' ')}</p>
        </motion.div>
      )}

      {/* Geo Status */}
      {geoStatus && !result && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-blue-400 text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          {geoStatus}
        </div>
      )}

      {/* Active Bookings */}
      {bookings.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <CheckCircle size={48} className="mx-auto mb-3 opacity-30" />
          <p className="text-lg">No active bookings to check in</p>
          <p className="text-sm mt-1">Book a session first, then come back here to check in</p>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((b, i) => (
            <motion.div
              key={b.id}
              {...fadeUp}
              transition={{ delay: i * 0.08, duration: 0.3 }}
              className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-5 hover:border-lime-400/20 transition-all"
              data-testid={`checkin-booking-${i}`}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-bold uppercase">
                      {b.session?.sport}
                    </span>
                    <span className="text-xs px-2.5 py-0.5 rounded-full bg-lime-500/20 text-lime-400 font-bold">
                      {b.stake_amount} $PLAY staked
                    </span>
                  </div>
                  <h4 className="text-white font-medium flex items-center gap-1.5 mb-1">
                    <MapPin size={14} className="text-slate-500" />
                    {b.session?.venue_name}
                  </h4>
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Clock size={12} />{new Date(b.session?.start_time).toLocaleString()}</span>
                    <span className="flex items-center gap-1"><Target size={12} />{b.session?.fill_rate}% filled</span>
                  </div>
                </div>

                <div className="flex gap-2 flex-shrink-0">
                  {/* GPS Check-in */}
                  <button
                    data-testid={`checkin-geo-btn-${i}`}
                    onClick={() => checkInWithGeo(b)}
                    disabled={checkingIn === b.id}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime-500 hover:bg-lime-400 text-black font-bold text-sm uppercase tracking-wide transition-all disabled:opacity-50 shadow-[0_0_15px_rgba(190,242,100,0.3)]"
                  >
                    {checkingIn === b.id ? (
                      <><Loader2 size={16} className="animate-spin" /> Verifying...</>
                    ) : (
                      <><Navigation size={16} /> GPS Check-In</>
                    )}
                  </button>

                  {/* QR Code */}
                  <button
                    data-testid={`checkin-qr-toggle-${i}`}
                    onClick={() => setShowQR(showQR === b.id ? null : b.id)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-slate-300 hover:bg-white/5 text-sm transition-all"
                  >
                    <QrCode size={16} />
                    QR
                  </button>
                </div>
              </div>

              {/* QR Code Display */}
              {showQR === b.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="mt-4 pt-4 border-t border-white/5"
                >
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="bg-white p-4 rounded-xl">
                      <QRCodeSVG
                        value={b.qr_code || `SETPLAY-CHECKIN-${b.id}`}
                        size={140}
                        level="H"
                      />
                    </div>
                    <div className="text-center md:text-left">
                      <p className="text-white text-sm font-medium mb-1">Show this QR code at the venue</p>
                      <p className="text-slate-400 text-xs">Scan at the venue kiosk or show to staff</p>
                      <p className="text-slate-600 text-[10px] font-mono mt-2">{b.qr_code}</p>
                      <button
                        data-testid={`checkin-qr-simulate-${i}`}
                        onClick={() => checkInWithQR(b)}
                        disabled={checkingIn === b.id}
                        className="mt-3 px-4 py-2 rounded-full bg-blue-600/20 text-blue-400 text-xs font-bold uppercase tracking-wide hover:bg-blue-600/30 transition-all disabled:opacity-50"
                      >
                        {checkingIn === b.id ? 'Verifying...' : 'Simulate QR Scan'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
